const CHUNK_IMPORT_RETRIES = 2;
const CHUNK_IMPORT_RETRY_DELAY_MS = 500;
const CHUNK_RELOAD_GUARD_KEY = "sw-chunk-load-reload-attempted-at";
export const CHUNK_RELOAD_COOLDOWN_MS = 10 * 60 * 1000;
const RELOAD_DELAY_MS = 0;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const importWithRetry = async <T>(
    importChunk: () => Promise<T>,
    retriesLeft = CHUNK_IMPORT_RETRIES,
): Promise<T> => {
    try {
        return await importChunk();
    } catch (error) {
        if (retriesLeft <= 0) {
            throw error;
        }
        await delay(CHUNK_IMPORT_RETRY_DELAY_MS);
        return importWithRetry(importChunk, retriesLeft - 1);
    }
};

// Chunk/module-load failure signatures across bundlers and browsers (webpack ChunkLoadError,
// Chromium/Safari/Firefox dynamic-import phrasings).
const CHUNK_LOAD_ERROR_PATTERNS = [
    /^ChunkLoadError$/i,
    /loading chunk .* failed/i,
    /failed to fetch dynamically imported module/i,
    /importing a module script failed/i,
    /error loading dynamically imported module/i,
];

const isChunkLoadError = (error: unknown): boolean => {
    if (!(error instanceof Error)) {
        return false;
    }
    return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(error.name) || pattern.test(error.message));
};

const readReloadGuard = (): number => {
    try {
        const value = Number(window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) ?? 0);
        return Number.isFinite(value) ? value : 0; // corrupt value — treat as no prior reload.
    } catch {
        return Infinity; // sessionStorage inaccessible — treat as on cooldown.
    }
};

const writeReloadGuard = (): boolean => {
    try {
        window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
        return true;
    } catch {
        return false;
    }
};

/**
 * Reloads once on a genuine chunk-load failure, giving the browser a fresh HTML/asset manifest.
 * Cooldown-guarded (not once-per-tab-forever) so a long-lived tab isn't left with zero recovery
 * budget after its first reload. Ignores non-chunk-load errors (e.g. a module throwing during
 * evaluation) since a reload can't fix those and would just hide them.
 */
export const reloadOnceOnChunkLoadFailure = (error?: unknown): boolean => {
    if (!isChunkLoadError(error)) {
        return false;
    }

    if (typeof window === "undefined") {
        return false;
    }

    if (Date.now() - readReloadGuard() < CHUNK_RELOAD_COOLDOWN_MS) {
        return false;
    }

    if (!writeReloadGuard()) {
        return false;
    }

    // Deferred a tick so an in-flight Sentry beacon isn't aborted by the navigation.
    setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    return true;
};
